// Core session management

export { convertToLlm } from "@caupulican/pi-agent-core";
// Compaction
export {
	type BranchPreparation,
	type BranchSummaryEntry,
	type BranchSummaryResult,
	buildSessionContext,
	type CollectEntriesResult,
	type CompactionEntry,
	type CompactionResult,
	CURRENT_SESSION_VERSION,
	type CustomEntry,
	type CustomMessageEntry,
	type CutPointResult,
	calculateContextTokens,
	collectEntriesForBranchSummary,
	compact,
	DEFAULT_COMPACTION_SETTINGS,
	estimateTokens,
	type FileEntry,
	type FileOperations,
	findCutPoint,
	findTurnStartIndex,
	type GenerateBranchSummaryOptions,
	generateBranchSummary,
	generateSummary,
	getLastAssistantUsage,
	getLatestCompactionEntry,
	type ModelChangeEntry,
	migrateSessionEntries,
	type NewSessionOptions,
	parseSessionEntries,
	prepareBranchEntries,
	type SessionContext,
	type SessionEntry,
	type SessionEntryBase,
	type SessionHeader,
	type SessionInfo,
	type SessionInfoEntry,
	SessionManager,
	type SessionMessageEntry,
	serializeConversation,
	shouldCompact,
	type ThinkingLevelChangeEntry,
} from "@caupulican/pi-agent-core/node";
export { type Args, parseArgs } from "./cli/args.ts";
// Config paths
export { getAgentDir, VERSION } from "./config.ts";
export {
	AgentSession,
	type AgentSessionConfig,
	type AgentSessionEvent,
	type AgentSessionEventListener,
	type ModelCycleResult,
	type ParsedSkillBlock,
	type PromptOptions,
	parseSkillBlock,
	type SessionStats,
} from "./core/agent-session.ts";
// Auth and model registry
export {
	type ApiKeyCredential,
	type AuthCredential,
	type AuthStatus,
	AuthStorage,
	type AuthStorageBackend,
	FileAuthStorageBackend,
	InMemoryAuthStorageBackend,
	type OAuthCredential,
} from "./core/auth-storage.ts";
// Copied bundled extensions resolve runtime dependencies through this public module. Bun embeds
// this surface; relative imports back into dist/core do not exist beside a compiled executable.
export {
	encodeWorkerSessionAllowedPaths,
	PI_WORKER_ALLOWED_PATHS_ENV,
} from "./core/autonomy/worker-session-private-scope.ts";
export { mapToolNamesForPlatform } from "./core/default-tool-surface.ts";
export { createEventBus, type EventBus, type EventBusController } from "./core/event-bus.ts";
// Extension system
export type {
	AgentEndEvent,
	AgentStartEvent,
	AgentToolResult,
	AgentToolUpdateCallback,
	AppKeybinding,
	AutocompleteProviderFactory,
	BashToolCallEvent,
	BeforeAgentStartEvent,
	BeforeAgentStartEventResult,
	BeforeProviderRequestEvent,
	BeforeProviderRequestEventResult,
	BuildSystemPromptOptions,
	CompactOptions,
	ContextEvent,
	ContextEventResult,
	ContextUsage,
	CustomToolCallEvent,
	EditToolCallEvent,
	ExecOptions,
	ExecResult,
	Extension,
	ExtensionActions,
	ExtensionAPI,
	ExtensionCommandContext,
	ExtensionCommandContextActions,
	ExtensionContext,
	ExtensionContextActions,
	ExtensionError,
	ExtensionEvent,
	ExtensionFactory,
	ExtensionFlag,
	ExtensionHandler,
	ExtensionRuntime,
	ExtensionShortcut,
	ExtensionStorage,
	ExtensionUIContext,
	ExtensionUIDialogOptions,
	ExtensionWidgetOptions,
	ExtensionWorkRunOptions,
	FindToolCallEvent,
	GrepToolCallEvent,
	InputEvent,
	InputEventResult,
	InputSource,
	KeybindingsManager,
	LoadExtensionsResult,
	LsToolCallEvent,
	MessageEndEvent,
	MessageRenderer,
	MessageRenderOptions,
	MessageStartEvent,
	MessageUpdateEvent,
	ProviderConfig,
	ProviderModelConfig,
	ReadToolCallEvent,
	RegisteredCommand,
	RegisteredTool,
	ResolvedCommand,
	SessionBeforeCompactEvent,
	SessionBeforeForkEvent,
	SessionBeforeSwitchEvent,
	SessionBeforeTreeEvent,
	SessionCompactEvent,
	SessionShutdownEvent,
	SessionStartEvent,
	SessionTreeEvent,
	SlashCommandInfo,
	SlashCommandSource,
	SourceInfo,
	TerminalInputHandler,
	ToolCallEvent,
	ToolCallEventResult,
	ToolDefinition,
	ToolExecutionEndEvent,
	ToolExecutionMode,
	ToolExecutionStartEvent,
	ToolExecutionUpdateEvent,
	ToolInfo,
	ToolRenderResultOptions,
	ToolResultEvent,
	TurnEndEvent,
	TurnStartEvent,
	UserBashEvent,
	UserBashEventResult,
	WidgetPlacement,
	WorkingIndicatorOptions,
	WriteToolCallEvent,
} from "./core/extensions/index.ts";
export {
	defineTool,
	ExtensionRunner,
	isBashToolResult,
	isEditToolResult,
	isFindToolResult,
	isGrepToolResult,
	isLsToolResult,
	isReadToolResult,
	isToolCallEventType,
	isWriteToolResult,
	wrapRegisteredTool,
	wrapRegisteredTools,
} from "./core/extensions/index.ts";
export { createExtensionRuntime, discoverAndLoadExtensions } from "./core/extensions/loader.ts";
// Footer data provider (git branch + extension statuses - data not otherwise available to extensions)
export type { ReadonlyFooterDataProvider } from "./core/footer-data-provider.ts";
export {
	appendRunRecord,
	type ConfidenceMode,
	type ConfidenceResult,
	cleanupImprovementSandbox,
	compareMetric,
	computeMadConfidence,
	createImprovementDecisionTool,
	createImprovementLoopTool,
	createImprovementSandbox,
	decideImprovement,
	exportImprovementSandboxPatch,
	type GitStatusEntry,
	type ImprovementDecision,
	type ImprovementDecisionInput,
	type ImprovementDecisionReason,
	type ImprovementDecisionResult,
	type ImprovementLoopConfig,
	type ImprovementLoopExec,
	type ImprovementLoopInitInput,
	type ImprovementLoopPaths,
	type ImprovementLoopRecordInput,
	type ImprovementLoopState,
	type ImprovementLoopStatusInput,
	type ImprovementLoopToolAction,
	type ImprovementLoopToolDetails,
	type ImprovementMeasurementCommandResult,
	type ImprovementMeasurementResult,
	type ImprovementRunRecord,
	type ImprovementSandboxCleanupInput,
	type ImprovementSandboxCreateInput,
	type ImprovementSandboxExportInput,
	type ImprovementSandboxRecord,
	improvementLoopPaths,
	initImprovementLoop,
	type LowConfidenceAction,
	type MetricComparison,
	type MetricDirection,
	median,
	medianAbsoluteDeviation,
	metricMapFromOutput,
	type OwnedDiscardPlan,
	type OwnedDiscardPlanInput,
	parseGitPorcelainStatus,
	parseMetricLines,
	planOwnedDiscard,
	readImprovementLoopState,
	readRunRecords,
	recordImprovementRun,
	runImprovementMeasurement,
	selectPrimaryMetric,
	serializeRunRecord,
} from "./core/improvement-loop.ts";
export { ModelRegistry } from "./core/model-registry.ts";
export * from "./core/orchestration/capability-gateway.ts";
export * from "./core/orchestration/contracts.ts";
export * from "./core/orchestration/event-store.ts";
export * from "./core/orchestration/model-binding.ts";
export * from "./core/orchestration/policy-compiler.ts";
export * from "./core/orchestration/profile-registry.ts";
export * from "./core/orchestration/profile-store.ts";
export * from "./core/orchestration/task-runtime.ts";
export { orchestrationThinkingLevelSchema } from "./core/orchestration/thinking-level-schema.ts";
export type {
	PackageManager,
	PathMetadata,
	ProgressCallback,
	ProgressEvent,
	ResolvedPaths,
	ResolvedResource,
} from "./core/package-manager.ts";
export { DefaultPackageManager } from "./core/package-manager.ts";
export { PI_ORCHESTRATION_AGENT_ID_ENV } from "./core/process-identity.ts";
export {
	ACTIVE_TURN_TTL_MS,
	AUTO_RELOAD_COORDINATOR_TTL_MS,
	describeReloadSession,
	getPendingReloadBlockers,
	getReloadCoordinationDir,
	isReloadSessionProcessAlive,
	type PendingReloadBlockers,
	type ReloadBlockerOptions,
	type ReloadSessionRecord,
} from "./core/reload-blockers.ts";
export type { ResourceCollision, ResourceDiagnostic, ResourceLoader } from "./core/resource-loader.ts";
export { DefaultResourceLoader, loadProjectContextFiles } from "./core/resource-loader.ts";
export {
	mergeResourceProfileMap,
	mergeResourceProfileSettings,
	parseResourceProfileBlocks,
	parseResourceProfileInput,
	parseResourceProfileJson,
	stripResourceProfileBlocks,
} from "./core/resource-profile-blocks.ts";
// SDK for programmatic usage
export {
	AgentSessionRuntime,
	type AgentSessionRuntimeDiagnostic,
	type AgentSessionRuntimeResource,
	type AgentSessionServices,
	type AgentSessionSwitchOptions,
	type CreateAgentSessionFromServicesOptions,
	type CreateAgentSessionOptions,
	type CreateAgentSessionResult,
	type CreateAgentSessionRuntimeFactory,
	type CreateAgentSessionRuntimeResult,
	type CreateAgentSessionServicesOptions,
	// Factory
	createAgentSession,
	createAgentSessionFromServices,
	createAgentSessionRuntime,
	createAgentSessionServices,
	createBashTool,
	// Tool factories (for custom cwd)
	createCodingTools,
	createEditTool,
	createFindTool,
	createGrepTool,
	createLsTool,
	createReadOnlyTools,
	createReadTool,
	createWriteTool,
	type PromptTemplate,
	SessionReplacementCallbackError,
	SessionReplacementRuntimeError,
} from "./core/sdk.ts";
export {
	BitwardenCredentialStorage,
	BitwardenCredentialStorageError,
	type BitwardenCredentialStorageErrorCode,
	type BitwardenCredentialStorageOptions,
} from "./core/secrets/bitwarden-credential-storage.ts";
export {
	BitwardenCredentialStorageRouter,
	type BitwardenCredentialStorageRouterOptions,
} from "./core/secrets/bitwarden-credential-storage-router.ts";
export {
	type BitwardenSecretsManagerCommandRequest,
	type BitwardenSecretsManagerCommandResult,
	BitwardenSecretsManagerCredentialStorage,
	type BitwardenSecretsManagerCredentialStorageOptions,
	BitwardenSecretsManagerStorageError,
} from "./core/secrets/bitwarden-secrets-manager-storage.ts";
export {
	type CredentialActivationResult,
	type CredentialConnectionStatus,
	CredentialManager,
	CredentialManagerError,
	type CredentialManagerErrorCode,
	type CredentialManagerOptions,
	type CredentialMutationResult,
	type CredentialProfileRecord,
	type CredentialProfileStorage,
	type CredentialProfileSummary,
	type CredentialProjectIdentity,
	type CredentialProjectResolver,
	CredentialStorageError,
	type CredentialStorageErrorCode,
	type CredentialStorageProvider,
	type CredentialVariable,
	type MachineCredentialSession,
	type MachineCredentialSessionResolver,
	type StoredCredentialProfileSummary,
} from "./core/secrets/credential-manager.ts";
export {
	type CredentialMigrationSource,
	CredentialMigrationSourceError,
	type CredentialMigrationSourceErrorCode,
	type CredentialMigrationSourceResolver,
	resolveCredentialMigrationSources,
} from "./core/secrets/credential-migration-source.ts";
export { createPortableGitProjectKey, resolveCredentialProject } from "./core/secrets/credential-project.ts";
export { WORKER_FORBIDDEN_TOOLS } from "./core/session-role.ts";
export {
	type CompactionSettings,
	type DirectoryResourceProfileInfo,
	getDirectoryResourceProfileInfo,
	type ImageSettings,
	matchesResourceProfilePattern,
	type PackageSource,
	type ResourceProfileFilterSettings,
	type ResourceProfileKind,
	type ResourceProfileSettings,
	type RetrySettings,
	SettingsManager,
	type SettingsManagerCreateOptions,
} from "./core/settings-manager.ts";
export {
	DEFAULT_SKILL_IDLE_TIMEOUT_MS,
	MAX_ACTIVE_SKILL_BODY_BYTES,
	MAX_LOADED_SKILLS,
	MAX_PINNED_SKILLS,
	MIN_ACTIVE_SKILL_BODY_BYTES,
	resolveActiveSkillBodyByteLimit,
	type SkillLoadResult,
	type SkillSearchResult,
	type SkillSlotStatus,
	SkillVaultController,
	type SkillVaultControllerOptions,
	type SkillVaultStatus,
} from "./core/skill-vault.ts";
// Skills
export {
	type LoadSkillsFromDirOptions,
	type LoadSkillsResult,
	loadSkills,
	loadSkillsFromDir,
	MAX_SKILL_FRONTMATTER_BYTES,
	readSkillFrontmatterFile,
	type Skill,
	type SkillFrontmatter,
} from "./core/skills.ts";
export { createSyntheticSourceInfo } from "./core/source-info.ts";
export { POLICY_OWNED_RUNTIME_TOOL_NAMES } from "./core/tool-capability-policy.ts";
export {
	type AskQuestion,
	type AskQuestionAnswer,
	AskQuestionDialog,
	type AskQuestionStopReason,
	type AskQuestionToolDetails,
	type AskQuestionToolInput,
	type AskQuestionToolOptions,
	createAskQuestionToolDefinition,
} from "./core/tools/ask-question.ts";
// Tools
export {
	type BashOperations,
	type BashSpawnContext,
	type BashSpawnHook,
	type BashToolDetails,
	type BashToolInput,
	type BashToolOptions,
	createBashToolDefinition,
	createEditToolDefinition,
	createFileFailureRecoveryAuthority,
	createFindToolDefinition,
	createGrepToolDefinition,
	createLocalBashOperations,
	createLsToolDefinition,
	createReadToolDefinition,
	createSecretStoreToolDefinition,
	createWriteToolDefinition,
	DEFAULT_MAX_BYTES,
	DEFAULT_MAX_LINES,
	type EditOperations,
	type EditToolDetails,
	type EditToolInput,
	type EditToolOptions,
	type FileContentReference,
	type FileFailureRecoveryAuthority,
	FileMutationIntentController,
	type FileMutationIntentControllerOptions,
	type FileMutationIntentOperations,
	type FileMutationKind,
	type FileMutationLease,
	type FileMutationPayloadReference,
	FileMutationPreflightError,
	type FileMutationPreflightFailureReason,
	type FilePathIdentity,
	type FilePathInspection,
	type FindOperations,
	type FindToolDetails,
	type FindToolInput,
	type FindToolOptions,
	formatMiddleOmissionMarker,
	formatSize,
	type GrepOperations,
	type GrepToolDetails,
	type GrepToolInput,
	type GrepToolOptions,
	type LsOperations,
	type LsToolDetails,
	type LsToolInput,
	type LsToolOptions,
	type ReadOperations,
	type ReadToolDetails,
	type ReadToolInput,
	type ReadToolOptions,
	type SecretStoreStatus,
	type SecretStoreToolDetails,
	type SecretStoreToolInput,
	type SecretStoreToolOptions,
	type ToolsOptions,
	type TruncationOptions,
	type TruncationResult,
	truncateHead,
	truncateKnownHeadTail,
	truncateLine,
	truncateMiddle,
	truncateTail,
	type WriteOperations,
	type WriteToolDetails,
	type WriteToolInput,
	type WriteToolOptions,
	withFileMutationQueue,
} from "./core/tools/index.ts";
export {
	createSkillVaultToolDefinition,
	type SkillToolDetails,
	type SkillToolInput,
} from "./core/tools/skill.ts";
// Skill-audit primitives — exported so extensions (e.g. continuous-learning) can reuse the native
// Jaccard overlap audit via `import { runSkillAudit } from "@caupulican/pi-adaptative"` instead of
// carrying a duplicate copy.
export {
	jaccard,
	runSkillAudit,
	type SkillAuditReport,
	type SkillSummary,
	tokenize,
} from "./core/tools/skill-audit.ts";
export { hasProjectTrustInputs, type ProjectTrustDecision, ProjectTrustStore } from "./core/trust-manager.ts";
// Main entry point
export { type MainOptions, main } from "./main.ts";
// Run modes for programmatic SDK usage
export {
	InteractiveMode,
	type InteractiveModeOptions,
	type ModelInfo,
	type PrintModeOptions,
	RpcClient,
	type RpcClientOptions,
	type RpcCommand,
	type RpcEventListener,
	type RpcResponse,
	type RpcSessionState,
	runPrintMode,
	runRpcMode,
} from "./modes/index.ts";
// UI components for extensions
export {
	ArminComponent,
	AssistantMessageComponent,
	BashExecutionComponent,
	BorderedLoader,
	BranchSummaryMessageComponent,
	CompactionSummaryMessageComponent,
	CustomEditor,
	CustomMessageComponent,
	DynamicBorder,
	ExtensionEditorComponent,
	ExtensionInputComponent,
	ExtensionSelectorComponent,
	FooterComponent,
	keyHint,
	keyText,
	LoginDialogComponent,
	ModelSelectorComponent,
	OAuthSelectorComponent,
	type RenderDiffOptions,
	rawKeyHint,
	renderDiff,
	renderTitleBadge,
	renderToolTitle,
	SessionSelectorComponent,
	type SettingsCallbacks,
	type SettingsConfig,
	SettingsSelectorComponent,
	ShowImagesSelectorComponent,
	SkillInvocationMessageComponent,
	ThemeSelectorComponent,
	ThinkingSelectorComponent,
	TitleBadgeComponent,
	type TitleBadgeOptions,
	type TitleBadgeSegment,
	type TitleBadgeStatus,
	ToolExecutionComponent,
	type ToolExecutionOptions,
	ToolTitleComponent,
	type ToolTitleOptions,
	type ToolTitleSegment,
	type ToolTitleStatus,
	TreeSelectorComponent,
	titleBadge,
	toolTitle,
	truncateToVisualLines,
	UserMessageComponent,
	UserMessageSelectorComponent,
	type VisualTruncateResult,
} from "./modes/interactive/components/index.ts";
// Theme utilities for custom tools and extensions
export {
	getLanguageFromPath,
	getMarkdownTheme,
	getSelectListTheme,
	getSettingsListTheme,
	highlightCode,
	initTheme,
	Theme,
	type ThemeColor,
} from "./modes/interactive/theme/theme.ts";
// Clipboard utilities
export { copyToClipboard } from "./utils/clipboard.ts";
export { parseFrontmatter, stripFrontmatter } from "./utils/frontmatter.ts";
export { convertToPng } from "./utils/image-convert.ts";
export { formatDimensionNote, type ResizedImage, resizeImage } from "./utils/image-resize.ts";
// Shell utilities
export { getShellConfig } from "./utils/shell.ts";
export {
	type AcquireWorkRunOptions,
	acquireWorkRun,
	assertPortablePathSegment,
	boundedWorkRetention,
	createWorkRunId,
	getProcessWorkRun,
	getWorkRoot,
	getWorkRunDir,
	getWorkTenantDir,
	PI_WORK_ROOT_ENV,
	pruneWorkTenant,
	type WorkRetentionOptions,
	type WorkRunLease,
} from "./utils/work-directory.ts";
